import { useCallback, useMemo, useState } from "react";
import moment from "moment-timezone";
import Modal from "../../../../../../components/Modal";
import DateInput from "../../../../../../components/Inputs/DateInput";
import { useVisible } from "../../../../../../utils/hooks/useVisible";
import { useTranslation } from "react-i18next";
import { getTimeSheetTimeZone, isAddedTimeSheet } from "../../../../components/TimeSheetTable/helpers";
import { applyTimezone, combineDateAndTime } from "../../../../../../utils/helpers/date";
import Button from "../../../../../../components/Button";
import { useAddedPayCode } from "../../../../components/TimeSheetTable/hooks/useAddedPayCode";
import "../../_style.scss";

export const useInOutDateModal = () => {
    const { t } = useTranslation();
    const { open, visible, close, selected } = useVisible();
    const [date, setDate] = useState();
    const payCode = useAddedPayCode(selected?.user);

    const onClose = useCallback(() => {
        setDate();
        close();
    }, [close]);

    const value = useMemo(() => {
        let _value;
        if (date) {
            _value = date;
        } else if (selected?.column === "in") {
            _value = selected?.original?.startDate;
        } else {
            _value = selected?.original?.endDate;
        }
        return moment.parseZone(_value).format("YYYY-MM-DD") ;
    }, [date, selected?.column, selected?.original?.endDate, selected?.original?.startDate]);

    const save = useCallback(() => {
        const {
            column,
            original,
            updateAddedTimeSheet,
            updateTimeSheet
        } = selected;
        const timeZone = getTimeSheetTimeZone(original);
        const updatedFields = { };
        let startDate = combineDateAndTime(
            moment(value),
            moment.parseZone(original.startDate),
        );
        let endDate = combineDateAndTime(
            moment(value),
            moment.parseZone(original.endDate),
        );
        startDate = applyTimezone(startDate, timeZone, true);
        endDate = applyTimezone(endDate, timeZone, true);
        
        if (column === "in" && original?.startDate) {
            if (original?.endDate) {
                if (startDate.date() !== moment.parseZone(original.endDate).date()) {
                    if (endDate.isAfter(startDate, "minute")) {
                        updatedFields.endDate = endDate.toISOString(true);
                    }
                }

                if (endDate.isBefore(startDate)) {
                    endDate = moment(endDate).add(1, "day");
                    updatedFields.endDate = endDate.toISOString(true);
                }
            }
            startDate = startDate.toISOString(true);

            const duration = original?.endDate && startDate
                ? moment(endDate).diff(moment(startDate), "seconds")
                : original?.duration || 0;

            updatedFields.startDate = startDate;
            updatedFields.duration = duration;
        }

        if (column === 'out' && original?.endDate) {
            const start = original?.startDate ? moment.parseZone(original.startDate) : null;
            let duration = 0;
            if (start && endDate.isBefore(start)) {
                endDate = endDate.add(1, "day");
            }

            updatedFields.endDate = endDate.toISOString(true);

            if (start) {
                duration = endDate.diff(start, "seconds") || 0;
                updatedFields.duration = duration;
            }
        }

        if (isAddedTimeSheet(original)) {
            if (!original?.payCode) {
                updatedFields.payCode = payCode;
            }
            updateAddedTimeSheet(original, updatedFields);
        } else {
            updateTimeSheet(original.id, updatedFields);
        }
        onClose();
    }, [selected, value, onClose, payCode]);

    const modal = useMemo(() => {
        return (
            <Modal
                isOpen={visible}
                toggle={onClose}
                centered
            >
                <div className="text-center w-100 h-100 p-4 in-out-date-calendar">
                    <h1 className="mb-3">{t("change-date")}</h1>
                    <DateInput
                        inline
                        value={moment(value).toDate()}
                        onChange={(value) => setDate(value)}
                        minDate={moment(selected?.original?.date).toDate()}
                        maxDate={moment(selected?.original?.date).add(1, "day").toDate()}
                    />
                </div>

                <div className="d-flex justify-content-end">
                    <Button color="muted" onClick={onClose}>
                        {t("cancel")}
                    </Button>

                    <Button color="primary" onClick={save}>
                        {t("save")}
                    </Button>
                </div>
            </Modal>
        )
    }, [visible, onClose, t, value, selected?.original?.date, save]);

    return { open, modal };
}